import { Box, Button, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { InputCustomPassword } from "../UIElements/input-custom-password";
import useResponsive from "../../hooks/useResponsive";
import { useMutation } from "@tanstack/react-query";
import { apiChangePassword } from "../../api/users";
import { ChangePasswordField } from "../../interface/users";
import { useAuth } from "../../hooks/useAuth";
import { useNotificationService } from "../notification";

const ChangePassword = () => {
  const isMedium = useResponsive("down", "md", "xs");
  const isSmall = useResponsive("down", "sm", "xl");
  const { token } = useAuth();
  const { showNotification } = useNotificationService();
  const [user_data, setUser_Data] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [validations, setValidations] = useState({
    length: false,
    number: false,
    specialChar: false,
    upperLowerCase: false,
  });

  useEffect(() => {
    setValidations({
      length: user_data.new_password.length >= 6,
      number: /\d/.test(user_data.new_password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(
        user_data.new_password
      ),
      upperLowerCase:
        /[a-z]/.test(user_data.new_password) &&
        /[A-Z]/.test(user_data.new_password),
    });
  }, [user_data.new_password]);

  const mutation = useMutation({
    mutationFn: (fieldValues: ChangePasswordField) =>
      apiChangePassword(fieldValues, token),
    onSuccess: (data) => {
      // Handle error
      if (data?.status === "error") {
        if ("msg" in data) {
          showNotification(data?.msg ?? "", "error");
          return;
        }
      }
      //  Handle success
      if ("data" in data) {
        showNotification(data?.data?.message ?? "", "success");
      }
    },
    onError: (error) => {
      console.error("Change password not successful", error);
      // Handle error
      showNotification("Change password not successful", "error");
    },
  });

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // check if the password is correct
    if (user_data.new_password != user_data.confirm_password) {
      showNotification("Passwords do not match", "warning");
      return;
    }
    if (
      user_data.current_password.length < 5 ||
      user_data.new_password.length < 5
    ) {
      showNotification("Old Password is needed, kindly add this", "warning");
      return;
    }
    mutation.mutate({
      old_password: user_data.current_password,
      new_password: user_data.new_password,
    });
  };
  return (
    <Box
      sx={{
        width: isSmall ? "96%" : isMedium ? "85%" : "550px",
        marginY: 4,
        marginX: 2,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: "#000E2C",
          fontWeight: 700,
          fontFamily: "Inter",
        }}
      >
        Change Password
      </Typography>

      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
        sx={{ my: 4 }}
      >
         <InputCustomPassword
          placeholder={"Enter current password"}
          value={user_data.current_password}
          onchange={(e: any) =>
            setUser_Data((prev) => ({
              ...prev,
              current_password: e.target.value,
            }))
          }
          styles={{ marginY: 2 }}
          label={"Current Password"}
        />
        <InputCustomPassword
          placeholder={"Enter new password"}
          value={user_data.new_password}
          onchange={(e: any) =>
            setUser_Data((prev) => ({
              ...prev,
              new_password: e.target.value,
            }))
          }
          styles={{ marginY: 2 }}
          label={"New - Password"}
        />
        <InputCustomPassword
          placeholder={"Confirm new password"}
          value={user_data.confirm_password}
          onchange={(e: any) =>
            setUser_Data((prev) => ({
              ...prev,
              confirm_password: e.target.value,
            }))
          }
          styles={{ marginY: 2 }}
          label={"Re-enter Password"}
        />
        <Stack direction="column" spacing={2} mt={3}>
          <ValidationItem
            isValid={validations.length}
            text="Must be more than 6 characters"
          />
          <ValidationItem
            isValid={validations.number}
            text="Contain a number"
          />
          <ValidationItem
            isValid={validations.specialChar}
            text="Contain a special character"
          />
          <ValidationItem
            isValid={validations.upperLowerCase}
            text="Contain Upper & lower case letters"
          />
        </Stack>
        <Box sx={{ marginTop: 4, fontFamily: "DM Sans" }}>
          <Button
            sx={{ width: "100%", borderRadius: "12px", fontWeight: 700 }}
            type="submit"
            variant="contained"
            color="primary"
          >
            Update Password
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ChangePassword;

interface ValidationItemProps {
  isValid: boolean;
  text: string;
}

const ValidationItem: React.FC<ValidationItemProps> = ({ isValid, text }) => {
  const styText = {
    fontSize: "10px",
    fontWeight: 500,
    fontFamily: "DM Sans",
    lineHeight: "13px",
    color: "#7D8590",
  };
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ margin: "14px 0" }}
    >
      {!isValid ? (
        <img src="/assets/icons/dot-icon.svg" alt="" width={6} height={6} />
      ) : (
        <img src="/assets/icons/dot-color.svg" alt="" width={6} height={6} />
      )}
      <Typography sx={styText}>{text}</Typography>
    </Stack>
  );
};
